import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import './ManageTeams.css';
import './ChatTeamsList.css';
import {
  connectSocket,
  emitMemberAdded,
} from '../services/socketService';
import TeamChat from './TeamChat';

const API_BASE = process.env.REACT_APP_API_URL || 'http://37.60.253.184:8081';

const ManageTeams = ({ onBack, token, userData, onTeamCreated }) => {
  const [loading, setLoading] = useState(true);
  const [showTeamNameModal, setShowTeamNameModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [teamNameInput, setTeamNameInput] = useState('');
  const [lawyers, setLawyers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLawyers, setSelectedLawyers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [filteredLawyers, setFilteredLawyers] = useState([]);
  const [chatGroups, setChatGroups] = useState([]);
  const [openChatGroup, setOpenChatGroup] = useState(null);

  // Socket setup
  useEffect(() => {
    if (!userData) return;
    const userName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || `User ${userData.id}`;
    connectSocket(userData.id, userName);
  }, [userData]);

  // Fetch chat groups
  const fetchChatGroups = useCallback(async () => {
    const authToken = token || (() => {
      try { return JSON.parse(localStorage.getItem('insafUser'))?.token; } catch { return null; }
    })();
    if (!authToken) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/chat/groups`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });
      const data = await res.json();
      if (res.ok && data.success && Array.isArray(data.data)) {
        setChatGroups(data.data);
      } else {
        setChatGroups([]);
      }
    } catch (err) {
      console.error('[ManageTeams] fetchChatGroups error:', err);
      setChatGroups([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch available lawyers
  const fetchLawyers = useCallback(async () => {
    const authToken = token || (() => {
      try { return JSON.parse(localStorage.getItem('insafUser'))?.token; } catch { return null; }
    })();
    if (!authToken) return;
    try {
      const response = await fetch(`${API_BASE}/api/guests/getLawyers`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });
      const data = await response.json();
      if (response.ok && data.data) {
        const lawyersList = Array.isArray(data.data) ? data.data : [data.data];
        setLawyers(lawyersList);
        setFilteredLawyers(lawyersList);
      }
    } catch (err) {
      console.error('Error fetching lawyers:', err);
    }
  }, [token]);

  // Mount
  useEffect(() => {
    fetchChatGroups();
    fetchLawyers();
  }, [fetchChatGroups, fetchLawyers]);

  // Search filter
  useEffect(() => {
    if (!showAddModal) return;
    if (searchTerm.trim() === '') {
      setFilteredLawyers(lawyers);
    } else {
      const q = searchTerm.toLowerCase();
      setFilteredLawyers(
        lawyers.filter(
          l =>
            (l.first_name && l.first_name.toLowerCase().includes(q)) ||
            (l.last_name && l.last_name.toLowerCase().includes(q)) ||
            (l.mobile_number && l.mobile_number.includes(searchTerm))
        )
      );
    }
  }, [searchTerm, showAddModal, lawyers]);

  const handleCreateTeam = () => {
    if (!teamNameInput.trim()) {
      Swal.fire({ title: 'Warning', text: 'Please enter a team name', icon: 'warning', confirmButtonColor: '#3085d6' });
      return;
    }
    setShowTeamNameModal(false);
    setShowAddModal(true);
  };

  const handleAddMember = async () => {
    if (!selectedLawyers || selectedLawyers.length === 0) {
      Swal.fire({ title: 'Warning', text: 'Please select at least one lawyer', icon: 'warning', confirmButtonColor: '#3085d6' });
      return;
    }
    if (!userData || !userData.id) {
      Swal.fire({ title: 'Error', text: 'User information not available. Please log in again.', icon: 'error', confirmButtonColor: '#3085d6' });
      return;
    }

    try {
      setAdding(true);
      const authToken = token || (() => {
        try { return JSON.parse(localStorage.getItem('insafUser'))?.token; } catch { return null; }
      })();
      const lawyerIds = selectedLawyers.map(l => l.id);
      const teamLeaderId = userData.id;

      const response = await fetch(`${API_BASE}/api/lawyer/add/my_team`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          user_id: [teamLeaderId],
          team_id: lawyerIds,
          name: teamNameInput.trim(),
        }),
      });
      const data = await response.json();

      if (response.ok) {
        // Create chat group
        let chatGroupId = null;
        try {
          const chatRes = await fetch(`${API_BASE}/api/chat/groups/create`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({ name: teamNameInput.trim(), member_ids: lawyerIds }),
          });
          const chatData = await chatRes.json();
          if (chatRes.ok && chatData.success && chatData.data?.group?.id) {
            chatGroupId = chatData.data.group.id;
          } else {
            console.warn('[ManageTeams] Chat group creation failed:', chatData.message);
          }
        } catch (chatErr) {
          console.error('[ManageTeams] Chat group create error:', chatErr);
        }

        // Socket notify
        if (chatGroupId) {
          selectedLawyers.forEach(lawyer => {
            emitMemberAdded(chatGroupId, {
              id: lawyer.id,
              first_name: lawyer.first_name || '',
              last_name: lawyer.last_name || '',
              profile_image: lawyer.profile_image || null,
              email: lawyer.email || '',
              mobile_number: lawyer.mobile_number || '',
            });
          });
        }

        // Notify Dashboard to open chat
        if (onTeamCreated && chatGroupId) {
          onTeamCreated({
            id: chatGroupId,
            name: teamNameInput.trim(),
            members_count: selectedLawyers.length + 1,
            members: selectedLawyers.map(l => ({
              id: l.id,
              first_name: l.first_name || '',
              last_name: l.last_name || '',
              profile_image: l.profile_image || null,
              email: l.email || '',
              mobile_number: l.mobile_number || '',
            })),
          });
        }

        Swal.fire({
          title: 'Success!',
          text: `Team "${teamNameInput.trim()}" created with ${selectedLawyers.length} member(s)!`,
          icon: 'success',
          confirmButtonColor: '#3085d6',
        });
        setShowAddModal(false);
        setSearchTerm('');
        setSelectedLawyers([]);
        setTeamNameInput('');
        setTimeout(() => fetchChatGroups(), 500);
      } else {
        Swal.fire({ title: 'Error', text: data.message || 'Failed to create team', icon: 'error', confirmButtonColor: '#3085d6' });
      }
    } catch (err) {
      console.error('Error creating team:', err);
      Swal.fire({ title: 'Error', text: err.message || 'Failed to create team', icon: 'error', confirmButtonColor: '#3085d6' });
    } finally {
      setAdding(false);
    }
  };

  const getAvatarUrl = img => {
    if (!img) return null;
    if (img.startsWith('http')) {
      return img.replace('http://37.60.253.184:8081', process.env.REACT_APP_API_URL || 'http://localhost:3001')
                .replace('https://37.60.253.184:8081', process.env.REACT_APP_API_URL || 'http://localhost:3001');
    }
    return `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/${img}`;
  };

  return (
    <div className="manage-teams-container">

      {/* Inline TeamChat */}
      {openChatGroup && (
        <TeamChat
          team={openChatGroup}
          userData={userData}
          token={token}
          onBack={() => setOpenChatGroup(null)}
          onGroupDeleted={() => { setOpenChatGroup(null); fetchChatGroups(); }}
          onGroupLeft={() => { setOpenChatGroup(null); fetchChatGroups(); }}
        />
      )}

      {/* Teams/Chats list */}
      {!openChatGroup && (
        <>
          {/* Header */}
          <div className="teams-header">
            <button className="back-button" onClick={onBack}>←</button>
            <h2>My Teams</h2>
            <div style={{ width: '40px' }} />
          </div>

          {/* Chat groups list */}
          <div className="teams-content chat-groups-content">
            {loading ? (
              <div className="loading-state"><p>Loading…</p></div>
            ) : chatGroups.length === 0 ? (
              <div className="no-teams">
                <span style={{ fontSize: 48 }}>💬</span>
                <p>No teams yet</p>
                <p style={{ fontSize: 13, color: '#aaa' }}>Tap + to create your first team</p>
              </div>
            ) : (
              <div className="chat-teams-list">
                {chatGroups.map(group => {
                  const avatarUrl = group.group_image ? getAvatarUrl(group.group_image) : null;
                  const initials = group.name?.charAt(0)?.toUpperCase() || 'T';
                  const memberLabel = `${group.members_count || 0} member${group.members_count !== 1 ? 's' : ''}`;
                  const latestMsg = group.latest_message || 'No messages yet';

                  return (
                    <div
                      key={group.id}
                      className="chat-team-item"
                      onClick={() => setOpenChatGroup(group)}
                    >
                      <div className="chat-team-avatar">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={group.name} onError={e => { e.target.style.display = 'none'; }} />
                        ) : (
                          <div className="chat-team-avatar-placeholder">{initials}</div>
                        )}
                      </div>
                      <div className="chat-team-info">
                        <p className="chat-team-name">{group.name}</p>
                        <p className="chat-team-sub">{latestMsg}</p>
                      </div>
                      <div className="chat-team-right">
                        <span className="chat-team-members">👥 {memberLabel}</span>
                        <span className="chat-team-chevron">›</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Floating Create Button */}
          <button
            className="floating-add-button"
            onClick={() => setShowTeamNameModal(true)}
            title="Create team"
          >
            +
          </button>

          {/* Set Team Name Modal */}
          {showTeamNameModal && (
            <div className="modal-overlay" onClick={() => setShowTeamNameModal(false)}>
              <div className="modal-content team-name-modal" onClick={e => e.stopPropagation()}>
                <h2 className="modal-title">Set Team Name</h2>
                <input
                  type="text"
                  className="team-name-input"
                  placeholder="Enter team name"
                  value={teamNameInput}
                  onChange={e => setTeamNameInput(e.target.value)}
                  maxLength={50}
                  onKeyPress={e => { if (e.key === 'Enter') handleCreateTeam(); }}
                  autoFocus
                />
                <div className="char-count">{teamNameInput.length}/50</div>
                <div className="modal-footer team-name-footer">
                  <button className="cancel-button" onClick={() => { setShowTeamNameModal(false); setTeamNameInput(''); }}>
                    Cancel
                  </button>
                  <button
                    className="submit-button confirm-button"
                    onClick={handleCreateTeam}
                    disabled={!teamNameInput.trim()}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add Members Modal */}
          {showAddModal && (
            <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Add Team Members</h3>
                  <button className="modal-close" onClick={() => setShowAddModal(false)}>✕</button>
                </div>

                <div className="modal-body">
                  {selectedLawyers.length > 0 && (
                    <div className="selected-count-banner">
                      <span>✓ {selectedLawyers.length} lawyer{selectedLawyers.length !== 1 ? 's' : ''} selected</span>
                      <button className="clear-selection-btn" onClick={() => setSelectedLawyers([])}>Clear All</button>
                    </div>
                  )}

                  <div className="search-container">
                    <span className="search-icon">🔍</span>
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search lawyer by name or phone..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {filteredLawyers.length > 0 && (
                    <div className="select-all-container">
                      <label className="select-all-label">
                        <input
                          type="checkbox"
                          checked={selectedLawyers.length === filteredLawyers.length && filteredLawyers.length > 0}
                          onChange={e => {
                            if (e.target.checked) setSelectedLawyers(filteredLawyers);
                            else setSelectedLawyers([]);
                          }}
                          className="lawyer-checkbox"
                        />
                        <span>Select All ({filteredLawyers.length})</span>
                      </label>
                    </div>
                  )}

                  <div className="dropdown-section">
                    {filteredLawyers.length > 0 ? (
                      <div className="dropdown-list">
                        {filteredLawyers.map(lawyer => {
                          const isSelected = selectedLawyers.some(l => l.id === lawyer.id);
                          const toggle = () => {
                            if (isSelected) setSelectedLawyers(selectedLawyers.filter(l => l.id !== lawyer.id));
                            else setSelectedLawyers([...selectedLawyers, lawyer]);
                          };
                          return (
                            <div
                              key={lawyer.id}
                              className={`dropdown-item ${isSelected ? 'selected' : ''}`}
                              onClick={toggle}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={toggle}
                                onClick={e => e.stopPropagation()}
                                className="lawyer-checkbox"
                              />
                              <div className="lawyer-avatar">
                                {lawyer.profile_image ? (
                                  <img
                                    src={lawyer.profile_image.startsWith('http') ? lawyer.profile_image : `${API_BASE}/${lawyer.profile_image}`}
                                    alt={lawyer.first_name}
                                    onError={e => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div className="avatar-placeholder">{lawyer.first_name?.charAt(0) || 'L'}</div>
                                )}
                              </div>
                              <div className="lawyer-info">
                                <p className="lawyer-name">{lawyer.first_name} {lawyer.last_name}</p>
                                <p className="lawyer-phone">{lawyer.mobile_number}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : searchTerm.trim() !== '' ? (
                      <p className="no-results">No lawyers found matching your search</p>
                    ) : (
                      <p className="loading-text">Loading lawyers…</p>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button className="cancel-button" onClick={() => { setShowAddModal(false); setSelectedLawyers([]); }}>
                    Cancel
                  </button>
                  <button
                    className="submit-button"
                    onClick={handleAddMember}
                    disabled={adding || selectedLawyers.length === 0}
                  >
                    {adding ? 'Creating…' : `Add ${selectedLawyers.length > 0 ? `${selectedLawyers.length} ` : ''}Member${selectedLawyers.length !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManageTeams;
